'use client'

/**
 * Hover (or tap) labels for the figures. Any descendant with a `data-tip`
 * attribute explains itself: the wrapper reads the attribute off whatever box
 * or bar is under the pointer and floats one small label next to it. One
 * listener per figure, no per-box state, works on SVG and plain divs alike.
 */

import { useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

const TIP_W = 260
/** Generous two-line estimate; the label is clamped by position, not measured. */
const TIP_H = 52

export function FigureTip({ children, className }: { children: ReactNode; className?: string }) {
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null)
  // On touch, pointerleave fires right after the tap; hold the label open for a beat instead.
  const holdUntil = useRef(0)

  const read = (e: PointerEvent<HTMLDivElement>) => {
    const el = (e.target as Element | null)?.closest?.('[data-tip]') as HTMLElement | SVGElement | null
    const text = el?.getAttribute('data-tip')
    if (!text) {
      if (tip) setTip(null)
      return
    }
    // Keep the label on screen: flip to the left of the pointer near the right edge, then clamp to the viewport (on a phone neither side fits).
    const flipped = e.clientX + 14 + TIP_W > window.innerWidth ? e.clientX - 14 - TIP_W : e.clientX + 14
    const x = Math.max(8, Math.min(flipped, window.innerWidth - TIP_W - 8))
    // Near the bottom (the phone's sticky bar lives there) the label goes above the pointer instead of below.
    const y = e.clientY + 16 + TIP_H > window.innerHeight ? e.clientY - 16 - TIP_H : e.clientY + 16
    setTip({ text, x, y })
    if (e.pointerType === 'touch') {
      holdUntil.current = Date.now() + 2200
      setTimeout(() => {
        if (Date.now() >= holdUntil.current) setTip(null)
      }, 2300)
    }
  }

  return (
    <div
      // Every labelled box: help cursor, and a lift on hover so you can see which one the label is about.
      className={cn(
        'relative [&_[data-tip]]:cursor-help [&_[data-tip]]:transition-[filter,opacity] [&_[data-tip]]:duration-100 [&_[data-tip]:hover]:brightness-150',
        className,
      )}
      onPointerMove={read}
      onPointerDown={read}
      onPointerLeave={() => {
        if (Date.now() >= holdUntil.current) setTip(null)
      }}
    >
      {children}
      {/* Portalled to body: a `backdrop-filter` ancestor (the mobile bar) would otherwise become the containing block for `fixed` and push the label off screen. */}
      {tip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-50 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs leading-snug text-popover-foreground shadow-[0_8px_24px_rgba(0,0,0,0.5)] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-100"
            style={{ left: tip.x, top: tip.y, maxWidth: TIP_W }}
          >
            {tip.text}
          </div>,
          document.body,
        )}
    </div>
  )
}
